/**
 * sl-ya4v.15: RenameBrandedSubdomainModal — confirm-step dialog for renaming
 * a workspace's branded subdomain.
 *
 * The modal is a thin shell over `SettingsEntityFormDialog`:
 *   - Old → new preview (formatted as `<old>.<apex>` → `<new>.<apex>`),
 *   - Inline format validation against the same regex the backend enforces,
 *   - Two notices the user MUST see before confirming (sl-xm1c.3.1 split
 *     — reuse cooldown + redirect lifetime are independent):
 *       1. Cooldown — the OLD label is reserved for 30 days (no other
 *          workspace can register it), and the OLD URL keeps 301-redirecting
 *          to the new effective host for up to 90 days. After day 30 the
 *          label can be reclaimed by any workspace; if reclaimed, the
 *          active match wins and the redirect ends early (host-resolver
 *          returns the new owner before consulting slug history).
 *       2. 30-day rename rate-limit — once renamed, the next rename is
 *          gated until `next + 30d`. Surfaced as the precise UTC instant
 *          (`nextRenameAt`) the backend returns from `availability` after
 *          the mutation succeeds.
 *
 * The modal does not call the rename mutation itself — the caller wires up
 * `useRenameBrandedSubdomain` and passes `onSubmit`. Errors raised by the
 * backend (reserved word, in-cooldown collision, rate-limit elapsed in a
 * different tab) propagate via `notify.error` in the caller.
 */
import { type ReactNode, useMemo, useState } from 'react';

import { SettingsEntityFormDialog } from '../shared/settings-form-dialog';
import * as styles from './styles.css';

// sl-ya4v.4b-rework: Mirror of `BRANDED_LABEL_PATTERN` in
// `packages/backend/server/src/utils/public-host.constants.ts`.
// 3-63 chars, lowercase a-z0-9 with internal hyphens — single source of
// truth lives backend-side; this client-side copy gates the Submit
// button so the user sees the rule before the network round-trip. The
// backend re-validates with the same regex + reserved-list lookup;
// nothing relies on this regex for security.
const LABEL_REGEX = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

export interface RenameBrandedSubdomainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current bare label (no apex), e.g. `acme`. */
  currentLabel: string;
  /** Full apex including leading nothing — e.g. `streamlain.site`. */
  apex: string;
  saving: boolean;
  onSubmit: (newLabel: string) => Promise<void>;
  /** Localised strings — passed in so the modal stays i18n-agnostic. */
  strings: RenameBrandedSubdomainModalStrings;
  testId?: string;
}

export interface RenameBrandedSubdomainModalStrings {
  title: ReactNode;
  fromLabel: ReactNode;
  toLabel: ReactNode;
  inputPlaceholder: string;
  formatHint: ReactNode;
  /** Format-error hint shown when the label fails the regex. */
  formatError: ReactNode;
  /** Identical-label error shown when the user typed the current label back. */
  identicalError: ReactNode;
  /**
   * Combined notice: "The old URL keeps redirecting for up to 90 days. The
   * old label is reserved for 30 days; after that any workspace can
   * reclaim it, which ends the redirect."
   * Reflects the sl-xm1c.3.1 split between reuse cooldown (30d, hard) and
   * redirect lifetime (90d, best-effort — capped by re-registration since
   * the host-resolver returns active matches before stale-slug history).
   */
  cooldownNotice: ReactNode;
  /** "Next rename available 30 days from now" rate-limit notice. */
  rateLimitNotice: ReactNode;
  saveLabel: ReactNode;
  savingLabel: ReactNode;
  cancelLabel: ReactNode;
}

export function RenameBrandedSubdomainModal({
  open,
  onOpenChange,
  currentLabel,
  apex,
  saving,
  onSubmit,
  strings,
  testId,
}: RenameBrandedSubdomainModalProps) {
  const [newLabel, setNewLabel] = useState('');
  const trimmed = newLabel.trim().toLowerCase();

  const validation = useMemo(() => {
    if (trimmed.length === 0) {
      return { isValid: false, error: null as ReactNode };
    }
    if (!LABEL_REGEX.test(trimmed)) {
      return { isValid: false, error: strings.formatError };
    }
    if (trimmed === currentLabel) {
      return { isValid: false, error: strings.identicalError };
    }
    return { isValid: true, error: null };
  }, [trimmed, currentLabel, strings.formatError, strings.identicalError]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setNewLabel('');
    }
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!validation.isValid || saving) return;
    void onSubmit(trimmed).then(() => {
      setNewLabel('');
    });
  };

  return (
    <SettingsEntityFormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={strings.title}
      saveLabel={strings.saveLabel}
      savingLabel={strings.savingLabel}
      cancelLabel={strings.cancelLabel}
      saving={saving}
      saveDisabled={!validation.isValid}
      onSave={handleSubmit}
      contentTestId={testId}
      saveTestId="rename-branded-subdomain-confirm"
    >
      <div className={styles.renameModalBody}>
        <div className={styles.renameModalRow}>
          <span className={styles.renameModalLabel}>{strings.fromLabel}</span>
          <code className={styles.renameModalHostFrom}>
            {currentLabel}.{apex}
          </code>
        </div>
        <div className={styles.renameModalRow}>
          <span className={styles.renameModalLabel}>{strings.toLabel}</span>
          <div className={styles.formInline}>
            <input
              type="text"
              className={styles.input}
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder={strings.inputPlaceholder}
              autoFocus
              data-testid="rename-branded-subdomain-input"
            />
            <span className={styles.inputSuffix}>.{apex}</span>
          </div>
        </div>
        <div className={styles.renameModalHint}>
          {validation.error ?? strings.formatHint}
        </div>
        <div className={styles.renameModalNotice}>{strings.cooldownNotice}</div>
        <div className={styles.renameModalNotice}>
          {strings.rateLimitNotice}
        </div>
      </div>
    </SettingsEntityFormDialog>
  );
}
